import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Fade,
  Container,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {  getApiUrl } from "../config";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}`;
const orbFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-18px)}`;
const scanL = keyframes`0%{transform:translateY(-100%)}100%{transform:translateY(3000%)}`;
const ripple = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,.25)}50%{box-shadow:0 0 0 10px rgba(59,130,246,0)}`;
const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;

const PALETTES = [
  {
    from: "#1d4ed8",
    to: "#60a5fa",
    shadow: "rgba(29,78,216,.28)",
    light: "#dbeafe",
    text: "#1e40af",
  },
  {
    from: "#6d28d9",
    to: "#a78bfa",
    shadow: "rgba(109,40,217,.26)",
    light: "#ede9fe",
    text: "#5b21b6",
  },
  {
    from: "#0369a1",
    to: "#38bdf8",
    shadow: "rgba(3,105,161,.26)",
    light: "#e0f2fe",
    text: "#075985",
  },
  {
    from: "#065f46",
    to: "#34d399",
    shadow: "rgba(6,95,70,.26)",
    light: "#d1fae5",
    text: "#064e3b",
  },
  {
    from: "#9f1239",
    to: "#fb7185",
    shadow: "rgba(159,18,57,.26)",
    light: "#ffe4e6",
    text: "#881337",
  },
  {
    from: "#92400e",
    to: "#fbbf24",
    shadow: "rgba(146,64,14,.26)",
    light: "#fef3c7",
    text: "#78350f",
  },
];
const ICONS = ["📐", "🔬", "📚", "🌍", "💻", "⚗️", "📊", "🏛️", "🧬", "🎨"];

// ── styled ──
const Root = styled(Box)({
  minHeight: "100vh",
  height: "100vh",
  background: "#f0f3f9",
  fontFamily: "'Sora',sans-serif",
  display: "flex",
  flexDirection: "column",
});

const TopBar = styled(Box)({
  background: "#07101f",
  borderBottom: "1px solid rgba(255,255,255,.06)",
  position: "sticky",
  top: 0,
  zIndex: 100,
  boxShadow: "0 4px 24px rgba(0,0,0,.25)",
});

const Hero = styled(Box)({
  background: "#07101f",
  position: "relative",
  overflow: "hidden",
  padding: "52px 0 88px",
  flexShrink: 0,
});

const HeroGrid = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(99,179,237,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,179,237,.04) 1px,transparent 1px)",
  backgroundSize: "52px 52px",
  pointerEvents: "none",
});

const HeroScan = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  height: 2,
  background:
    "linear-gradient(90deg,transparent,rgba(96,165,250,.12),transparent)",
  animation: `${scanL} 8s linear infinite`,
  pointerEvents: "none",
});

const HeroOrb = styled(Box)(({ l, t, size, color, delay }) => ({
  position: "absolute",
  left: l,
  top: t,
  width: size,
  height: size,
  borderRadius: "50%",
  background: color,
  filter: "blur(88px)",
  opacity: 0.48,
  animation: `${orbFloat} ${8 + (delay || 0)}s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
  pointerEvents: "none",
}));

const WaveCut = styled(Box)({
  position: "absolute",
  bottom: -2,
  left: 0,
  right: 0,
  height: 64,
  background: "#f0f3f9",
  clipPath: "ellipse(58% 100% at 50% 100%)",
});

const ExamBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "rgba(255,255,255,.06)",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: 100,
  padding: "6px 16px",
  backdropFilter: "blur(8px)",
  marginBottom: 20,
});

const SubCard = styled(Box)(({ pal, selected }) => ({
  borderRadius: 20,
  border: selected ? `2px solid ${pal.from}` : "1.5px solid #e2e8f0",
  background: "#fff",
  boxShadow: selected
    ? `0 0 0 4px ${pal.shadow}`
    : "0 2px 12px -4px rgba(15,23,42,.07)",
  transition: "all .32s cubic-bezier(.22,1,.36,1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-9px)",
    boxShadow: `0 22px 52px -12px ${pal.shadow}`,
    borderColor: "transparent",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg,${pal.from},${pal.to})`,
    opacity: selected ? 1 : 0,
    transition: "opacity .32s ease",
  },
  "&:hover::after": { opacity: 1 },
}));

const BeginBtn = styled(Button)(({ pal }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".82rem",
  padding: "9px 16px",
  background: `linear-gradient(105deg,${pal.from},${pal.to})`,
  backgroundSize: "200% auto",
  color: "#fff",
  boxShadow: `0 6px 18px -6px ${pal.shadow}`,
  transition: "all .28s ease",
  "&:hover": {
    animation: `${shimmer} 1s linear infinite`,
    transform: "translateY(-2px)",
    boxShadow: `0 12px 24px -6px ${pal.shadow}`,
  },
}));

const LogoutBtn = styled(Button)({
  borderRadius: 9,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 600,
  fontSize: ".8rem",
  color: "rgba(255,255,255,.55)",
  border: "1.5px solid rgba(255,255,255,.1)",
  padding: "6px 14px",
  transition: "all .22s ease",
  "&:hover": {
    background: "rgba(239,68,68,.15)",
    borderColor: "rgba(239,68,68,.4)",
    color: "#f87171",
  },
});

const InsRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "11px 0",
  borderBottom: "1px solid #f1f5f9",
  "&:last-child": { borderBottom: "none" },
});

// ── component ──
export default function WaecSubjectScreen() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [examName, setExamName] = useState("Exam Subjects");
  const [examImage, setExamImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const API_URL = getApiUrl();

    const token = JSON.parse(localStorage.getItem("authToken"));
    if (!token) {
      navigate("/student-login");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/waec-subjects/`, {
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Failed to load subjects.");
        setExamName(d.exam_name || "Exam Subjects");
        setExamImage(d.exam_image || "");
        setSubjects(d.subjects || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.clear();
      navigate("/student-login");
    }
  };
  const startExam = () => {
    if (selectedId) navigate(`/waec-questions?subjectId=${selectedId}`);
  };

  const instructions = [
    "Ensure you have a stable internet connection before starting.",
    "Do not close the browser or turn off your device during the exam.",
    "The exam will auto-submit when the timer reaches zero.",
    "Manage your time wisely across all questions.",
    "You may use a rough sheet for calculations.",
  ];

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.6,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "rgba(255,255,255,.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all .2s ease",
                  "&:hover": { background: "rgba(255,255,255,.14)" },
                }}
              >
                <ArrowBackIcon
                  sx={{ color: "rgba(255,255,255,.7)", fontSize: 18 }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "9px",
                    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SchoolIcon sx={{ color: "#fff", fontSize: 17 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 700,
                    color: "#e2e8f0",
                    fontSize: ".92rem",
                  }}
                >
                  LiteUpper
                </Typography>
              </Box>
            </Box>
            <LogoutBtn
              onClick={handleLogout}
              startIcon={<LogoutIcon sx={{ fontSize: ".95rem !important" }} />}
            >
              Sign Out
            </LogoutBtn>
          </Box>
        </Container>
      </TopBar>

      {/* HERO */}
      <Hero>
        <HeroGrid />
        <HeroScan />
        <HeroOrb
          l="-120px"
          t="-100px"
          size="400px"
          color="radial-gradient(circle,rgba(37,99,235,.5),transparent)"
          delay={0}
        />
        <HeroOrb
          l="62%"
          t="20%"
          size="260px"
          color="radial-gradient(circle,rgba(16,185,129,.25),transparent)"
          delay={2}
        />
        <HeroOrb
          l="25%"
          t="55%"
          size="180px"
          color="radial-gradient(circle,rgba(124,58,237,.2),transparent)"
          delay={3}
        />
        <WaveCut />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={700}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              {examImage ? (
                <Box
                  component="img"
                  src={examImage}
                  alt="exam"
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "22px",
                    objectFit: "cover",
                    border: "3px solid rgba(255,255,255,.2)",
                    boxShadow: "0 16px 40px rgba(0,0,0,.3)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "22px",
                    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 16px 40px rgba(37,99,235,.35)",
                  }}
                >
                  <SchoolIcon sx={{ color: "#fff", fontSize: 40 }} />
                </Box>
              )}
              <Box>
                <ExamBadge>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#4ade80",
                      boxShadow: "0 0 8px #4ade80",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      color: "rgba(255,255,255,.7)",
                      fontSize: ".74rem",
                      fontWeight: 600,
                    }}
                  >
                    {subjects.length} subject{subjects.length !== 1 ? "s" : ""}{" "}
                    available
                  </Typography>
                </ExamBadge>
                <Typography
                  sx={{
                    fontFamily: "'DM Serif Display',serif",
                    fontSize: { xs: "1.8rem", md: "2.3rem" },
                    color: "#fff",
                    lineHeight: 1.14,
                    mb: 0.5,
                  }}
                >
                  {examName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    color: "rgba(255,255,255,.5)",
                    fontSize: ".88rem",
                  }}
                >
                  Select a subject below to begin your examination
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Hero>

      {/* CONTENT */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: "'DM Serif Display',serif",
                fontSize: "1.5rem",
                color: "#0f172a",
                mb: 0.4,
              }}
            >
              Available Subjects
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#94a3b8",
                fontSize: ".84rem",
              }}
            >
              Click any subject to review instructions and begin your exam
            </Typography>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 14,
                gap: 2,
              }}
            >
              <CircularProgress
                size={46}
                thickness={4}
                sx={{ color: "#3b82f6" }}
              />
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#94a3b8",
                  fontSize: ".86rem",
                }}
              >
                Loading subjects…
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                textAlign: "center",
                py: 12,
                background: "#fff",
                borderRadius: "22px",
                border: "1.5px solid #fee2e2",
              }}
            >
              <Typography sx={{ fontSize: "2.5rem", mb: 2 }}>⚠️</Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  color: "#dc2626",
                  fontSize: ".95rem",
                }}
              >
                {error}
              </Typography>
            </Box>
          ) : subjects.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2,1fr)",
                  md: "repeat(3,1fr)",
                },
                gap: 3,
              }}
            >
              {subjects.map((subject, i) => {
                const pal = PALETTES[i % PALETTES.length];
                const icon = ICONS[i % ICONS.length];
                const isSelected = selectedId === subject.id;
                return (
                  <Fade in timeout={300 + i * 80} key={subject.id}>
                    <Box
                      sx={{
                        animation: `${fadeUp} ${
                          0.4 + i * 0.07
                        }s cubic-bezier(.22,1,.36,1) both`,
                        animationDelay: `${i * 0.06}s`,
                      }}
                    >
                      <SubCard
                        pal={pal}
                        selected={isSelected}
                        onClick={() => setSelectedId(subject.id)}
                      >
                        <Box sx={{ p: "24px 24px 20px" }}>
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "16px",
                              background: `linear-gradient(135deg,${pal.from},${pal.to})`,
                              boxShadow: `0 10px 24px -6px ${pal.shadow}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "1.45rem",
                              mb: 2.2,
                            }}
                          >
                            {icon}
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              fontWeight: 700,
                              fontSize: ".98rem",
                              color: "#0f172a",
                              mb: 0.45,
                            }}
                          >
                            {subject.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              color: "#94a3b8",
                              fontSize: ".78rem",
                              mb: 2.8,
                            }}
                          >
                            Click to review instructions & begin
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                background: pal.light,
                                color: pal.text,
                                fontFamily: "'Sora',sans-serif",
                                fontSize: ".68rem",
                                fontWeight: 700,
                                px: 1.3,
                                py: 0.45,
                                borderRadius: 100,
                                letterSpacing: ".06em",
                              }}
                            >
                              EXAM
                            </Box>
                            <BeginBtn
                              pal={pal}
                              variant="contained"
                              endIcon={
                                <PlayArrowIcon
                                  sx={{ fontSize: ".9rem !important" }}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedId(subject.id);
                              }}
                            >
                              Begin
                            </BeginBtn>
                          </Box>
                        </Box>
                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              background: pal.from,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: `0 4px 12px ${pal.shadow}`,
                              animation: `${ripple} 2s ease-in-out infinite`,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{ color: "#fff", fontSize: 14 }}
                            />
                          </Box>
                        )}
                      </SubCard>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 13,
                background: "#fff",
                borderRadius: "22px",
                border: "1.5px dashed #dbeafe",
              }}
            >
              <Typography sx={{ fontSize: "3rem", mb: 2 }}>📭</Typography>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.4rem",
                  color: "#0f172a",
                  mb: 1,
                }}
              >
                No subjects yet
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "#94a3b8",
                  fontSize: ".86rem",
                }}
              >
                No active subjects available at this time.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* INSTRUCTIONS MODAL */}
      {selectedId && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.72)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 300,
            p: 2,
          }}
          onClick={() => setSelectedId(null)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              maxWidth: 480,
              background: "#fff",
              borderRadius: "24px",
              overflow: "hidden",
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.28)",
              animation: `${fadeUp} .35s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            {/* Modal header */}
            <Box
              sx={{
                background: "#07101f",
                p: "28px 32px 24px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <HeroGrid />
              <HeroOrb
                l="-60px"
                t="-60px"
                size="180px"
                color="radial-gradient(circle,rgba(37,99,235,.4),transparent)"
              />
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".7rem",
                  fontWeight: 700,
                  color: "rgba(255,255,255,.4)",
                  letterSpacing: ".09em",
                  textTransform: "uppercase",
                  mb: 0.6,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Before you begin
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'DM Serif Display',serif",
                  fontSize: "1.6rem",
                  color: "#fff",
                  lineHeight: 1.2,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {subjects.find((s) => s.id === selectedId)?.name || "Exam"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  color: "rgba(255,255,255,.42)",
                  fontSize: ".82rem",
                  mt: 0.4,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Exam Instructions
              </Typography>
            </Box>

            {/* Instructions */}
            <Box sx={{ p: "24px 32px 6px" }}>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: ".68rem",
                  color: "#475569",
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                  mb: 1.5,
                }}
              >
                Please read carefully
              </Typography>
              {instructions.map((ins, i) => (
                <InsRow key={i}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "8px",
                      background: "#dbeafe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.15,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        fontSize: ".7rem",
                        color: "#2563eb",
                      }}
                    >
                      {i + 1}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".86rem",
                      color: "#334155",
                      lineHeight: 1.62,
                    }}
                  >
                    {ins}
                  </Typography>
                </InsRow>
              ))}
              <Box
                sx={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "11px",
                  p: "10px 14px",
                  mt: 1.5,
                  mb: 0.5,
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={{ fontSize: ".9rem" }}>⚠️</Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".8rem",
                    color: "#92400e",
                    lineHeight: 1.6,
                  }}
                >
                  Once started, the timer <b>cannot be paused</b>. Ensure you're
                  fully ready before proceeding.
                </Typography>
              </Box>
            </Box>

            {/* Actions */}
            <Box sx={{ p: "16px 32px 28px", display: "flex", gap: 2 }}>
              <Button
                onClick={() => setSelectedId(null)}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  py: 1.35,
                  fontSize: ".86rem",
                  color: "#64748b",
                  borderColor: "#e2e8f0",
                  borderWidth: 1.5,
                  "&:hover": { background: "#f8faff", borderColor: "#cbd5e1" },
                }}
              >
                Go Back
              </Button>
              <Button
                onClick={startExam}
                fullWidth
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.35,
                  fontSize: ".86rem",
                  background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                  boxShadow: "0 10px 24px -6px rgba(37,99,235,.38)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 14px 30px -6px rgba(37,99,235,.48)",
                  },
                  transition: "all .28s ease",
                }}
              >
                Start Exam
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Root>
  );
}
